import { useEffect, useCallback, useMemo } from "react";
import { FormActions } from "../../components/common/FormActions";
import FormFields from "../../components/common/FormFields";
import { PARKING_RATE } from "../../utils/constants";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  createColumn,
  createEditAction,
  createViewAction,
  getForm,
  getRegister,
  getResultOptions,
  formatDateTime,
  numberFormat,
} from "../../utils/helpers";
import { URL_PARKING_RATES } from "../../utils/urls";
import useGet from "../common/useGet";
import {
  API_PARKINGS,
  API_CONFIGURABLES,
  API_PARKING_RATES,
  API_DISTRICTS,
} from "../../utils/apis";

export const useParkingRates = () => {
  // Get parkings data
  const {
    loadData: loadParkingData,
    loading: loadingParkings,
    data: parkingsData,
  } = useGet();

  const {
    loadData: loadDistrict,
    loading: loadingDistricts,
    data: districtsData,
  } = useGet();

  // Get parking hours configurables
  const {
    loadData: loadParkingHours,
    loading: loadingParkingHours,
    data: parkingHoursData,
  } = useGet();

  // Get car size configurables
  const {
    loadData: loadCarSizes,
    loading: loadingCarSizes,
    data: carSizesData,
  } = useGet();

  useEffect(() => {
    loadParkingData({ api: API_PARKINGS });
    loadDistrict({ api: API_DISTRICTS });
    loadParkingHours({
      api: API_CONFIGURABLES,
      params: {
        key: "parking_hours",
      },
    });
    loadCarSizes({
      api: API_CONFIGURABLES,
      params: {
        key: "car_size",
      },
    });
  }, []);

  const tableColumns = useCallback(
    () => [
      createColumn("location", true, "District Name"),
      createColumn("hour", false),
      createColumn("car_size", true),
      createColumn("amount", false),
      createColumn("actions", false),
    ],
    [],
  );

  const tableData = useCallback((data: any) => {
    if (!data) return [];

    return data.map((item: any) => {
      return {
        ...item,
        parking_name: item?.location,
        amount: numberFormat(item?.amount),
        hour: item?.hour,
        car_size: item?.car_size,
        actions: (
          <FormActions
            actions={[
              createViewAction(URL_PARKING_RATES, item?.id),
              createEditAction(URL_PARKING_RATES, item?.id),
            ]}
          />
        ),
      };
    });
  }, []);

  const viewData = useCallback((item: any) => {
    // Handle case where item is undefined or null
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "Parking Rate Details",
          fields: {
            parking_location: item?.location || "N/A",
            parking_hours: item?.hour || "N/A",
            car_size: item?.car_size || "N/A",
            amount: item?.amount
              ? `${Number(item.amount).toLocaleString("en-US")}`
              : "N/A",
          },
        },
        {
          title: "System Information",
          fields: {
            record_id: item?.id || "N/A",
            created_by: item?.created_by
              ? `${item.created_by.first_name} ${item.created_by.last_name}`
              : "System",
            created_at: item?.created_at
              ? formatDateTime(item.created_at)
              : "N/A",
            updated_at: item?.updated_at
              ? formatDateTime(item.updated_at)
              : "N/A",
            updated_by: item?.updated_by
              ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
              : "Not updated",
          },
        },
      ],
    };
  }, []);

  const formRoute = useMemo(() => getForm(URL_PARKING_RATES), []);
  const activityFilter = useMemo(() => "parking_ratess", []);

  return {
    tableData,
    tableColumns,
    viewData,
    formRoute,
    activityFilter,
    parkingsData,
    parkingHoursData,
    carSizesData,
    loadingParkings,
    districtsData,
    loadingParkingHours,
    loadingDistricts,
    loadingCarSizes,
  };
};

export const useParkingRateForm = ({
  id,
  updateFormValues,
  isView = false,
}: TFormHookParams & { isView?: boolean } = {}) => {
  // Get districts data
  const {
    loadData: loadDistrict,
    loading: loadingDistricts,
    data: districtsData,
  } = useGet();

  // Get parking hours configurables
  const {
    loadData: loadParkingHours,
    loading: loadingParkingHours,
    data: parkingHoursData,
  } = useGet();

  // Get car size configurables
  const {
    loadData: loadCarSizes,
    loading: loadingCarSizes,
    data: carSizesData,
  } = useGet();

  // Get existing parking rates data (only for list view)
  const { loadData, loading, data } = useGet();

  // Get specific parking rate data for view/edit
  const {
    loadData: loadSingleData,
    loading: loadingSingle,
    data: singleData,
  } = useGet();

  // Load all required data only once
  useEffect(() => {
    loadDistrict({ api: API_DISTRICTS });
    loadParkingHours({
      api: API_CONFIGURABLES,
      params: {
        key: "parking_hours",
      },
    });
    loadCarSizes({
      api: API_CONFIGURABLES,
      params: {
        key: "car_size",
      },
    });

    // Only load all rates if not viewing/editing a specific item
    if (!id) {
      loadData({ api: API_PARKING_RATES });
    }
  }, []);

  // Load specific item data only when ID changes
  useEffect(() => {
    if (id) {
      loadSingleData({ api: `${API_PARKING_RATES}/${id}` });
    }
  }, [id]);

  // Set street_type only once
  useEffect(() => {
    if (updateFormValues) {
      updateFormValues({
        street_type: PARKING_RATE,
      });
    }
  }, [updateFormValues]);

  // Memoize form sections to prevent unnecessary re-renders
  const sections: IFormSection[] = useMemo(
    () => [
      {
        title: "Parking Rate Details",
        fields: isView
          ? [
              {
                field_name: "parking_name",
                label: "Parking",
                type: "text",
                props: { readonly: true },
              },
              {
                field_name: "hours",
                label: "Hours",
                type: "text",
                props: { readonly: true },
              },
              {
                field_name: "car_size",
                label: "Car Size",
                type: "text",
                props: { readonly: true },
              },
              {
                field_name: "amount",
                label: "Amount",
                type: "text",
                props: { readonly: true },
              },
            ]
          : [
              {
                field_name: "location_id",
                label: "District",
                type: "select",
                props: { required: true },
                options_data: getResultOptions(districtsData, "name"),
                loading: loadingDistricts,
              },
              {
                field_name: "hour_id",
                label: "Hours",
                type: "select",
                props: { required: true },
                options_data: getResultOptions(parkingHoursData, "name"),
                loading: loadingParkingHours,
              },
              {
                field_name: "car_size_id",
                label: "Car Size",
                type: "select",
                props: { required: true },
                options_data: getResultOptions(carSizesData, "name"),
                loading: loadingCarSizes,
              },
              {
                field_name: "amount",
                label: "Amount",
                type: "number",
                props: {
                  required: true,
                  minValue: 500,
                  step: 0.01,
                  placeholder: "Enter amount",
                },
              },
            ],
      },
    ],
    [
      isView,
      districtsData,
      parkingHoursData,
      carSizesData,
      loadingDistricts,
      loadingParkingHours,
      loadingCarSizes,
    ],
  );

  // Memoize options to prevent unnecessary re-renders
  const options = useMemo(
    () => ({
      parking_hours: getResultOptions(parkingHoursData, "name"),
      car_sizes: getResultOptions(carSizesData, "name"),
    }),
    [parkingHoursData, carSizesData],
  );

  const title = useMemo(
    () => (isView ? "View Parking Rate" : "Parking Rate Management"),
    [isView],
  );
  const redirect = useMemo(() => getRegister(URL_PARKING_RATES), []);

  return {
    sections,
    title,
    redirect,
    options,
    loading: loading || loadingSingle,
    data,
    singleData,
    districtsData,
    parkingHoursData,
    carSizesData,
    loadingParkingHours,
    loadingDistricts,
    loadingCarSizes,
    isView,
  };
};

export const ParkingRateForm = ({
  form,
  error,
  updateFormValue,
  onChange,
}: any) => {
  const { sections } = useParkingRateForm({
    form,
    updateFormValues: updateFormValue,
  });

  return (
    <div>
      {sections.map((section: IFormSection, index: number) => (
        <div key={index}>
          <h3>{section.title}</h3>
          <FormFields
            form={form}
            error={error}
            updateFormValue={updateFormValue}
            onChange={onChange}
            fields={section.fields}
          />
        </div>
      ))}
    </div>
  );
};
